import { useFormContext } from 'react-hook-form'

import { Typography } from '@/components/typography/typography'
import { useReservation } from '@/features/reservation'
import { cn } from '@/libs/tw'
import { translate } from '@/utils/translator'

import { useBookingForWatcher } from '../../hooks/useBookingForWatcher'
import { FormValues, bookingForValues, companies } from '../../schema'
import { FormDropdownInput } from '../inputs/formDropdownInput'
import { FormRadioInput } from '../inputs/formRadioInput'
import { FormTextInput } from '../inputs/formTextInput'
import { FormTextareaInput } from '../inputs/formTextareaInput'
import { AddressForm } from './addressForm'
import { GuestsForRoomForm } from './guestsForRoomForm'

export const GuestDataForm = () => {
	const { data: reservation } = useReservation()

	const {
		formState: { errors },
	} = useFormContext<FormValues>()

	useBookingForWatcher()

	if (!reservation) return null

	return (
		<div
			className={cn(
				'flex w-full flex-col gap-48 rounded-8 text-userData-textMobile md:w-[752px] md:bg-naturel md:p-24 md:text-userData-text'
			)}
		>
			<div className={cn('flex flex-col gap-24')}>
				<Typography style={'h3'} element="h3">
					{translate('guest-data-contact')}
				</Typography>
				<div className={cn('grid grid-cols-1 gap-16 md:grid-cols-2')}>
					<FormTextInput
						name="email"
						label={translate('guest-data-email')}
						required
						error={errors.email?.message}
						autoComplete="email"
					/>
					<FormTextInput
						name="phoneNumber"
						label={translate('guest-data-phoneNumber')}
						required
						error={errors.phoneNumber?.message}
						autoComplete="tel"
					/>
				</div>
			</div>
			<div className={cn('grid grid-cols-1 gap-16 md:grid-cols-2')}>
				<FormTextInput
					name="name"
					label={translate('guest-data-name')}
					required
					error={errors.name?.message}
					autoComplete="given-name"
				/>
				<FormTextInput
					name="surname"
					label={translate('guest-data-surname')}
					required
					error={errors.surname?.message}
					autoComplete="family-name"
				/>
			</div>
			<AddressForm />
			{reservation.employeeBookingDetails && (
				<div className={cn('flex flex-col gap-24')}>
					<FormDropdownInput
						name={`employeesPromotion.company` as keyof FormValues}
						label={translate('guest-data-company')}
						required
						error={errors.employeesPromotion?.company?.message}
						options={companies}
					/>
					<FormTextInput
						name={`employeesPromotion.employeeName` as keyof FormValues}
						label={translate('guest-data-employee-name')}
						required
						error={errors.employeesPromotion?.employeeName?.message}
					/>
					<FormTextInput
						name={`employeesPromotion.companyRelationship` as keyof FormValues}
						label={translate('guest-data-company-relationship')}
						required
						error={errors.employeesPromotion?.companyRelationship?.message}
					/>
					<Typography style={'p-ms'} element="p" className="-mt-16">
						{translate('guest-data-employee-code-hint')}
					</Typography>
				</div>
			)}
			<div className={cn('flex flex-col gap-24')}>
				<Typography style={'h3'} element="h3">
					{translate('guest-data-booking-for')}
				</Typography>
				<div className={cn('grid grid-cols-1 gap-16 md:grid-cols-2')}>
					<FormRadioInput
						name="bookingFor"
						value={bookingForValues.ME}
						label={translate('guest-data-bookingFor-me')}
					/>
					<FormRadioInput
						name="bookingFor"
						value={bookingForValues.OTHER}
						label={translate('guest-data-bookingFor-other')}
					/>
				</div>
			</div>
			{reservation.roomStays.map((room, roomIndex) => (
				<GuestsForRoomForm index={roomIndex} guests={room.guests} />
			))}
			<div className={cn('flex flex-col gap-24')}>
				<Typography style={'h3'} element="h3">
					{translate('guest-data-annotation')}
				</Typography>
				<FormTextareaInput
					name="annotation"
					placeholder={translate('guest-data-annotation-placeholder')}
					error={errors.annotation?.message}
				/>
			</div>
		</div>
	)
}
